import { elementAttributes } from "../html-attributes";
import { parseStyleAttr } from "../html-parse";
import { EMPTY_STYLE_MAP, type StyleMap } from "../shared-block-styles";
import { EmailBlockDefinition } from "../types";

import { LuCode } from "react-icons/lu";

import { claimableTag, hasBlockLevelChild } from "./claim";
import {
  htmlFallbackEmailRenderer,
  htmlRawEmailRenderer,
  htmlTextEmailRenderer,
} from "./email-renderer";
import { RAW_COMMENT_ATTRIBUTE } from "./prepare-html";
import { HtmlFallbackTiptapView, HtmlRawTiptapView } from "./tiptap-view";

export interface HtmlFallbackAttrs extends Record<string, unknown> {
  /** The element's own tag. Empty on a wrapper ProseMirror synthesised. */
  tag: string;
  attributes: Record<string, string>;
  style: StyleMap;
}

export interface HtmlRawAttrs extends Record<string, unknown> {
  /** The markup verbatim, comment delimiters and all. */
  html: string;
}

/**
 * `tag: "*"` at the lowest priority.
 *
 * `Element.matches("*")` is what backs this, so every element reaches the rule
 * — and every typed block, mark and grid rule is sorted ahead of it, so a
 * paragraph is still a paragraph and a `<tr>` is still a row. What lands here
 * is only what nothing else wanted, which until now meant deletion.
 */
const FALLBACK_PRIORITY = 0;

/** Above the `*` rules, below every typed block. */
const RAW_PRIORITY = 1;

/**
 * A preserved element that holds structure: the `<div>` that constrains a page
 * to 600px, the `<section>` that paints a band, the `<center>` an older
 * template still uses.
 */
export const htmlBlockBlock: EmailBlockDefinition<HtmlFallbackAttrs> = {
  name: "htmlBlock",
  label: "HTML block",
  icon: LuCode,
  category: "layout",

  schema: {
    attrs: {
      tag: { default: "", jsonOnly: true },
      attributes: { default: {} },
      style: { default: EMPTY_STYLE_MAP },
    },
    // Everything a cell can hold, plus the synthetic text wrapper. `htmlText`
    // is listed first so loose text inside a preserved container is wrapped in
    // one rather than gaining a paragraph's margins and default type.
    content: "(htmlText | emailBlock | emailRow | bulletList | orderedList)*",
    group: "emailBlock",
    draggable: true,
  },

  parseHTML: () => [
    {
      tag: "*",
      priority: FALLBACK_PRIORITY,
      getAttrs: (node) => {
        const element = node as HTMLElement;
        const tag = claimableTag(element);
        if (!tag || !hasBlockLevelChild(element)) return false;
        return {
          tag,
          attributes: elementAttributes(element),
          style: parseStyleAttr(element),
        };
      },
    },
  ],

  tiptapComponent: HtmlFallbackTiptapView,
  emailRenderer: htmlFallbackEmailRenderer,
};

/**
 * A preserved element that holds text.
 *
 * Also the wrapper ProseMirror reaches for when loose text turns up inside an
 * `htmlBlock`. That wrapper has no `tag`, which is how the renderer knows to
 * emit the text without inventing an element around it — otherwise parsing the
 * output would produce a document one level deeper than the one it came from,
 * and every reopened draft would drift further.
 */
export const htmlTextBlock: EmailBlockDefinition<HtmlFallbackAttrs> = {
  name: "htmlText",
  label: "HTML text",
  icon: LuCode,
  category: "content",

  schema: {
    attrs: {
      tag: { default: "", jsonOnly: true },
      attributes: { default: {} },
      style: { default: EMPTY_STYLE_MAP },
    },
    content: "inline*",
    group: "emailBlock",
    draggable: true,
  },

  parseHTML: () => [
    {
      tag: "*",
      priority: FALLBACK_PRIORITY,
      getAttrs: (node) => {
        const element = node as HTMLElement;
        const tag = claimableTag(element);
        // The two rules are exclusive on purpose: whichever the parser tries
        // first, an element lands on exactly one of them.
        if (!tag || hasBlockLevelChild(element)) return false;
        return {
          tag,
          attributes: elementAttributes(element),
          style: parseStyleAttr(element),
        };
      },
    },
  ],

  tiptapComponent: HtmlFallbackTiptapView,
  emailRenderer: htmlTextEmailRenderer,
};

/**
 * Markup that is not an element: a `<style>` block, and the conditional
 * comments a designer writes for Outlook.
 *
 * `<style>` is in ProseMirror's default ignore list, so a template's class
 * rules were dropped along with everything they styled; comments never reach
 * rule matching at all, which is why `prepareEmailHtml` rewrites them into
 * elements before parsing.
 */
export const htmlRawBlock: EmailBlockDefinition<HtmlRawAttrs> = {
  name: "htmlRaw",
  label: "Raw HTML",
  icon: LuCode,
  category: "layout",

  schema: {
    attrs: {
      html: { default: "", jsonOnly: true },
    },
    content: undefined,
    group: "emailBlock",
    draggable: false,
    isolating: true,
  },

  parseHTML: () => [
    {
      tag: "style",
      priority: RAW_PRIORITY,
      getAttrs: (node) => ({ html: (node as HTMLElement).outerHTML }),
    },
    {
      tag: `div[${RAW_COMMENT_ATTRIBUTE}]`,
      priority: RAW_PRIORITY,
      getAttrs: (node) => ({
        html: `<!--${(node as HTMLElement).getAttribute(RAW_COMMENT_ATTRIBUTE) ?? ""}-->`,
      }),
    },
  ],

  tiptapComponent: HtmlRawTiptapView,
  emailRenderer: htmlRawEmailRenderer,
};
